import { models, stores } from '@kurtosys/app-start';
import { EventTypes } from '@kurtosys/event-manager/dist/models/EventTypes.js';
import { components } from '@kurtosys/ksys-app-components';
import { common, query } from '@kurtosys/ksys-app-template';
import { IQueryKeyValue } from '@kurtosys/types/query/IQueryKeyValue.js';
import { action, computed, makeObservable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { MockData } from '../../../configuration/mockData/index.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { IFactRedirect } from '../../../models/app/IFactRedirect.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents.js';

const { isNullOrUndefined, pivotCollection } = common.commonUtils;
const { RedirectHelper } = common.helpers;

import { IManifest } from '@kurtosys/types/appsManager/index.js';
type IFactListProps = components.fact.FactList.models.IFactListProps;
type QueryStore = stores.QueryStore<IConfiguration, IComponentStyles>;

/* [Component: appStoreComponentImport] */

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	static EVENT_BUS_KEY: EventTypes = EventTypes.GLOBAL_INPUTS_CHANGE;
	url: URL;
	rawUrl: string;

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		this.url = new URL(url);
		this.rawUrl = url;
		makeObservable(this);
	}

	@computed
	get hasData(): boolean {
		if (this.factListProps) {
			const { itemBlankDataOptions, items } = this.factListProps;
			if (itemBlankDataOptions && itemBlankDataOptions.showBlank) {
				return true;
			}
			const valid =
				items &&
				items.reduce((hasValues: boolean, item) => {
					let itemValue;
					if (item.riskRatingProps) {
						itemValue = item.riskRatingProps.value;
					} else if (item.morningstarRatingProps) {
						itemValue = item.morningstarRatingProps.morningStarRating;
					} else {
						itemValue = item.value;
					}
					if (!isNullOrUndefined(itemValue)) {
						hasValues = true;
					}
					return hasValues;
				}, false);
			return valid === true;
		}
		return false;
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	};

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get factRedirects(): IFactRedirect[] | undefined {
		const factRedirects = this.appComponentConfiguration && this.appComponentConfiguration.factRedirects;
		return factRedirects;
	}

	@computed
	get factListProps(): IFactListProps | undefined {
		const factList = this.appComponentConfiguration && this.appComponentConfiguration.factList;
		const factListProps = this.mergeQueriesAndProps(factList) as IFactListProps;
		return this.orchestrateFactRedirects(factListProps);
	}

	getRedirectHelper() {
		const redirectHelper = new RedirectHelper({ queryClass: query.Query }, this.rawUrl);
		return redirectHelper;
	}

	orchestrateFactRedirects = (factListProps: IFactListProps | undefined) => {
		const factRedirects = this.factRedirects;
		if (factListProps && factListProps.items && factListProps.items.length > 0 && factRedirects) {
			const redirectsById = pivotCollection(
				factRedirects,
				(factRedirect) => factRedirect.factId || '__NO_FACT_ID',
			);
			factListProps.items.forEach((item) => {
				if (item && item.dataType === 'link' && item.linkProps && item.linkProps.id) {
					const factRedirect = redirectsById[item.linkProps.id];
					if (factRedirect) {
						const { redirect, inputs: redirectInputs = [] } = factRedirect;
						if (redirect) {
							const redirectHelper = this.getRedirectHelper();
							const inputQueriesByKey = redirectInputs.reduce((redirectConfigInputs, inputConfig) => {
								if (!redirectConfigInputs[inputConfig.key]) {
									redirectConfigInputs[inputConfig.key] = inputConfig.query;
								}
								return redirectConfigInputs;
							}, {} as IQueryKeyValue);

							const inputs = query.queryUtils.queryHelper.convertQueryKeyValue(
								{ queryClass: query.Query },
								inputQueriesByKey || {},
								this.queryStore.executionOptions,
							);
							const redirectPath = redirectHelper.getRedirectPath(redirect, inputs);
							if (!item.linkProps) {
								item.linkProps = {};
							}
							item.linkProps.href = redirectPath;
						}
					}
				}
			});
		}
		return factListProps;
	};

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	async customInitializeAfter() {
		// TODO: Initialize stores here. See https://github.com/Kurtosys/studio-apps/blob/7c5456503e664db90e39a1f8fd8f9edd38f892b6/apps/ksys-app-global-inputs/src/components/App/stores/AppStore.ts#L197
	}
}
